import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  Checkbox,
  LinearProgress,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  TableSortLabel,
  Menu,
  ListItemIcon,
  ListItemText,
  Chip,
  Autocomplete,
  Collapse,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Computer as ComputerIcon,
  Memory as MemoryIcon,
  ContentCopy as CopyIcon,
  AutoFixHigh as AutoFillIcon,
  ArrowDropDown as ArrowDropDownIcon,
  PlaylistAdd as PlaylistAddIcon,
  PowerSettingsNew as PowerIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowRight as ExpandRightIcon,
  Cloud as CloudIcon
} from '@mui/icons-material';
import api, { QuotaIncreaseItem, RegionSyncOperationResponse } from '../services/api';
import { Provider, AzureReservationSummary } from '../types/providers';
import { filterUiSupportedProviders } from '../utils/uiSupportedProviders';
import { useNotification } from '../contexts/NotificationContext';
import { CapacityDetail, RegionLimitCell } from './regions/CapacityBars';
import ReservationManager, { ReservationSummaryCell } from './regions/ReservationManager';
import ConfirmDialog from './regions/ConfirmDialog';
import ProviderErrorsPanel from './ProviderErrorsPanel';

const BULK_BATCH_SIZE = 5;
const BATCH_DELAY_MS = 100;
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Columns the regions table can be sorted by. `status`, `capacity` and
// `reservations` are backed by data joined in from the capacityData /
// azureReservations maps rather than fields on the Region row.
type SortField =
  | 'region_code'
  | 'status'
  | 'capacity'
  | 'reservations'
  | 'machine_count'
  | 'configured';

interface RegionManagementProps {
  selectedProviderId?: string;
}

interface Region {
  id?: string;
  name: string;
  region_code: string;
  location?: string;
  continent?: string;
  configured: boolean;
  machine_count: number;
  provider_id?: string;
  image_id_amd64?: string;
  image_id_arm64?: string;
}

interface RegionCreateRequest {
  name: string;
  region_code: string;
  location: string;
  continent: string;
  configured: boolean;
  image_id_amd64?: string;
  image_id_arm64?: string;
}

interface RegionUpdateRequest {
  name?: string;
  region_code?: string;
  location?: string;
  continent?: string;
  configured?: boolean;
  image_id_amd64?: string;
  image_id_arm64?: string;
}

// Checkbox list shown in the "Sync regions" dialog so the operator picks exactly which
// newly-discovered regions to add (Azure's Locations API returns everything, including
// canary "*euap"/staging "*stg"/restricted-access regions that are rarely wanted). The
// current selection is mirrored into `selectionRef` so the confirm dialog's onConfirm
// closure — captured when the dialog opened — reads the latest picks at click time.
const RegionAddSelector: React.FC<{
  regions: string[];
  selectionRef: React.MutableRefObject<Set<string>>;
}> = ({ regions, selectionRef }) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(regions));
  useEffect(() => {
    selectionRef.current = selected;
  }, [selected, selectionRef]);

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Button size="small" onClick={() => setSelected(new Set(regions))}>Select all</Button>
        <Button size="small" onClick={() => setSelected(new Set())}>Clear</Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {selected.size} of {regions.length} selected
        </Typography>
      </Box>
      <Box
        sx={{
          maxHeight: 240,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1,
        }}
      >
        {regions.map((code) => (
          <FormControlLabel
            key={code}
            sx={{ m: 0 }}
            control={
              <Checkbox
                size="small"
                checked={selected.has(code)}
                onChange={() => toggle(code)}
              />
            }
            label={<Typography variant="body2">{code}</Typography>}
          />
        ))}
      </Box>
    </Box>
  );
};

const RegionManagement: React.FC<RegionManagementProps> = ({ selectedProviderId: propSelectedProviderId }) => {
  const { showNotification } = useNotification();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<string>(propSelectedProviderId || '');
  // Tracks the last prop value we've already applied so we only react to genuine
  // external prop changes and never revert a user's local dropdown selection.
  const prevPropProviderIdRef = useRef(propSelectedProviderId);
  const unmountedRef = useRef(false);
  // Operation ids currently being polled, so a reservation op is never polled twice
  // (e.g. once by the triggering handler and again by the load-time resume effect).
  const activeReservationPollsRef = useRef<Set<string>>(new Set());
  // Operator's chosen subset of regions to add in the "Sync regions" dialog; mirrored
  // from RegionAddSelector so the confirm closure reads the latest selection at click time.
  const selectedToAddRef = useRef<Set<string>>(new Set());
  // Reset on setup as well as cleanup: React 18 StrictMode mounts, unmounts, then
  // re-mounts in dev, so a cleanup-only effect would leave unmountedRef stuck `true`
  // for the live mount — stalling polls and the "reset busy flag" guards below.
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(false);
  const [releasingReservations, setReleasingReservations] = useState(false);
  const [syncingCapacity, setSyncingCapacity] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{ synced: number; total: number } | null>(null);
  const [syncingRegions, setSyncingRegions] = useState(false);
  const [syncMenuAnchor, setSyncMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRegions, setSelectedRegions] = useState<Set<string>>(new Set());
  const [regionFilters, setRegionFilters] = useState<string[]>([]);
  // Sort state. Sortable columns span both the region row itself
  // (region_code, machine_count, configured) and data joined in from the
  // capacity/reservation maps (status, capacity, reservations), so the field
  // is a dedicated union rather than `keyof Region`.
  const [sortField, setSortField] = useState<SortField>('region_code');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkCreateDialogOpen, setBulkCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [bulkRegionsText, setBulkRegionsText] = useState('');
  // Bulk reserve: distribute a total slot count equally across the selected regions.
  const [bulkReserveDialogOpen, setBulkReserveDialogOpen] = useState(false);
  const [bulkReserveTotal, setBulkReserveTotal] = useState('');
  const [bulkReserveIncludeIpv6, setBulkReserveIncludeIpv6] = useState(false);
  // When true, ignore the total field and reserve each selected region up to its
  // own available capacity instead of dividing a single total equally.
  const [bulkReserveMaxMode, setBulkReserveMaxMode] = useState(false);
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
  const [createForm, setCreateForm] = useState<RegionCreateRequest>({
    name: '',
    region_code: '',
    location: '',
    continent: '',
    configured: false,
    image_id_amd64: '',
    image_id_arm64: ''
  });
  const [editForm, setEditForm] = useState<RegionUpdateRequest>({});
  const [inlineEdit, setInlineEdit] = useState<{
    regionId: string;
    field: 'image_id_amd64' | 'image_id_arm64';
    value: string;
  } | null>(null);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [capacityData, setCapacityData] = useState<Map<string, any>>(new Map());
  const [azureReservations, setAzureReservations] = useState<Map<string, AzureReservationSummary>>(new Map());
  const [manualLimitDrafts, setManualLimitDrafts] = useState<Record<string, string>>({});
  const [manualLimitDirty, setManualLimitDirty] = useState<Set<string>>(new Set());
  const [manualLimitSavingIds, setManualLimitSavingIds] = useState<Set<string>>(new Set());
  const [quotaIncreaseIds, setQuotaIncreaseIds] = useState<Set<string>>(new Set());
  // Region rows whose detail panel is expanded.
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  // Regions with an in-flight reservation operation (reserve/scale-down/release).
  const [reservationBusyIds, setReservationBusyIds] = useState<Set<string>>(new Set());
  // Live progress for an in-flight reservation operation, keyed by region id.
  const [reservationProgress, setReservationProgress] = useState<
    Map<string, { phase: string; completed: number; total: number }>
  >(new Map());
  // Single themed confirmation dialog, replacing the native window.confirm().
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message?: React.ReactNode;
    confirmLabel?: string;
    confirmColor?: 'primary' | 'error' | 'warning';
    onConfirm: () => void;
  } | null>(null);

  const requestConfirm = useCallback((opts: {
    title: string;
    message?: React.ReactNode;
    confirmLabel?: string;
    confirmColor?: 'primary' | 'error' | 'warning';
    onConfirm: () => void;
  }) => {
    setConfirmState({ ...opts, open: true });
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmState((prev) => (prev ? { ...prev, open: false } : prev));
  }, []);

  const toggleRegionExpanded = useCallback((regionId?: string) => {
    if (!regionId) return;
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) {
        next.delete(regionId);
      } else {
        next.add(regionId);
      }
      return next;
    });
  }, []);

  const setReservationBusy = useCallback((regionId: string, busy: boolean) => {
    setReservationBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) {
        next.add(regionId);
      } else {
        next.delete(regionId);
      }
      return next;
    });
  }, []);

  const setRegionProgress = useCallback((
    regionId: string,
    progress: { phase: string; completed: number; total: number } | null,
  ) => {
    setReservationProgress((prev) => {
      const next = new Map(prev);
      if (progress) {
        next.set(regionId, progress);
      } else {
        next.delete(regionId);
      }
      return next;
    });
  }, []);

  const showSnackbar = useCallback((
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info',
    duration: number | null = 5000,
  ) => {
    showNotification(message, severity, duration ?? 6000);
  }, [showNotification]);

  const normalizeRegionFilters = useCallback((values: string[]) => {
    const normalized = new Set<string>();
    values.forEach((value) => {
      value
        .split(/[,&]/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => normalized.add(part));
    });
    return Array.from(normalized);
  }, []);

  const regionFilterOptions = useMemo(() => {
    const options = new Set<string>();
    regions.forEach((region) => {
      const prefix = region.region_code?.split('-')[0];
      if (prefix) {
        options.add(prefix);
      }
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [regions]);

  const selectedProvider = useMemo(
    () => providers.find((provider) => provider.id === selectedProviderId),
    [providers, selectedProviderId]
  );

  // Total free (releasable) reservations across every region of the selected provider.
  // Drives the "Release All Reservations" button's enabled state so it can't fire a
  // no-op when nothing is reservable.
  const totalFreeReservations = useMemo(() => {
    let total = 0;
    azureReservations.forEach((summary) => {
      total += summary.free ?? 0;
    });
    return total;
  }, [azureReservations]);

  const normalizedRegionFilters = useMemo(
    () => regionFilters.map((filter) => filter.trim().toLowerCase()).filter(Boolean),
    [regionFilters]
  );

  const filteredRegions = useMemo(() => {
    if (normalizedRegionFilters.length === 0) {
      return regions;
    }
    return regions.filter((region) => {
      const haystack = [
        region.region_code,
        region.name,
        region.location,
        region.continent
      ]
        .filter(Boolean)
        .map((value) => value?.toLowerCase() ?? '');
      return normalizedRegionFilters.some((filter) =>
        haystack.some((value) => value.includes(filter))
      );
    });
  }, [regions, normalizedRegionFilters]);

  // Column totals shown in the table footer, computed over the rows currently
  // visible (i.e. after region filtering). "Total capacity" is the summed
  // instance limit across all regions; "Enabled capacity" is the same sum
  // restricted to configured (enabled) regions; "Reserved capacity" is the
  // summed reservation total.
  const capacityTotals = useMemo(() => {
    let totalCapacity = 0;
    let enabledCapacity = 0;
    let reservedCapacity = 0;
    filteredRegions.forEach((region) => {
      const capacity = region.id ? capacityData.get(region.id) : undefined;
      const limit = capacity
        ? capacity.effective_instance_limit ?? capacity.instance_limit ?? 0
        : 0;
      totalCapacity += limit;
      if (region.configured) {
        enabledCapacity += limit;
      }
      const summary = region.id ? azureReservations.get(region.id) : undefined;
      reservedCapacity += summary?.total ?? 0;
    });
    return { totalCapacity, enabledCapacity, reservedCapacity };
  }, [filteredRegions, capacityData, azureReservations]);

  const loadCapacityData = useCallback(async () => {
    if (!selectedProviderId) return;

    try {
      const capacityList = await api.getProviderRegionsCapacity(selectedProviderId);
      const capacityMap = new Map();
      capacityList.forEach(cap => {
        capacityMap.set(cap.region_id, cap);
      });
      setCapacityData(capacityMap);
    } catch (error: any) {
      console.error('Failed to load capacity data:', error);
    }
  }, [selectedProviderId]);

  const loadAzureReservations = useCallback(async () => {
    const reservationsSupported =
      selectedProvider?.provider_type === 'azure' || selectedProvider?.provider_type === 'gcp';
    if (!selectedProviderId || !reservationsSupported || regions.length === 0) {
      setAzureReservations(new Map());
      return;
    }
    const next = new Map<string, AzureReservationSummary>();
    try {
      const summaries = await api.listAzureProviderReservations(selectedProviderId);
      summaries.forEach((summary) => {
        if (summary.region_id) next.set(summary.region_id, summary);
      });
    } catch (error) {
      console.error('Failed to load Azure reservations', error);
    }
    setAzureReservations(next);
  }, [regions.length, selectedProvider?.provider_type, selectedProviderId]);

  // Shared polling loop — call after a sync job is queued or when resuming after navigation.
  // Uses explicit providerId and jobId so polling continues even if selection or closure state changes.
  const pollCapacitySyncJob = useCallback(async (
    providerId: string,
    jobId: string,
    isCancelledRef: React.MutableRefObject<boolean>,
  ) => {
    const maxPollAttempts = 90;
    const pollIntervalMs = 2000;
    let finalResult: any = null;

    let lastProcessed = 0;
    for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
      await delay(pollIntervalMs);
      if (isCancelledRef.current) return;
      try {
        const job = await api.getProviderCapacitySyncJob(providerId, jobId);
        finalResult = job;
        // Count successes + failures so the counter advances even on errors
        const processed = job.regions_synced + (job.errors?.length ?? 0);
        if (job.total_regions > 0) {
          setSyncProgress({ synced: processed, total: job.total_regions });
        }
        // Refresh the capacity table whenever new regions have been synced
        if (processed > lastProcessed) {
          lastProcessed = processed;
          loadCapacityData();
        }
        if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') break;
      } catch {
        break;
      }
    }

    if (isCancelledRef.current) return;

    if (finalResult?.status === 'cancelled') {
      showSnackbar(
        `Capacity sync cancelled — ${finalResult.regions_synced}/${finalResult.total_regions} regions synced before cancellation`,
        'warning',
        null,
      );
    } else if (finalResult?.status === 'completed') {
      const hasErrors = finalResult.errors.length > 0;
      showSnackbar(
        `Capacity sync complete — ${finalResult.regions_synced}/${finalResult.total_regions} regions` +
        (hasErrors ? ` · ${finalResult.errors.length} error${finalResult.errors.length > 1 ? 's' : ''}` : ''),
        hasErrors ? 'warning' : 'success',
        null,
      );
    } else if (finalResult?.status === 'failed') {
      showSnackbar(
        finalResult.errors[0] || finalResult.message || 'Capacity sync failed',
        'error',
        null,
      );
    } else if (finalResult) {
      showSnackbar('Capacity sync is still running in the background', 'warning');
    }

    await loadCapacityData();
    setSyncingCapacity(false);
  }, [showSnackbar, loadCapacityData]);

  const handleSyncCapacity = async () => {
    if (!selectedProviderId) {
      showSnackbar('No provider selected', 'warning');
      return;
    }

    try {
      setSyncingCapacity(true);
      setSyncProgress(null);
      const result = await api.syncProviderCapacity(selectedProviderId);

      if (result.total_regions > 0) {
        setSyncProgress({ synced: 0, total: result.total_regions });
      }

      if (result.job_id) {
        await pollCapacitySyncJob(selectedProviderId, result.job_id, unmountedRef);
        return;
      }

      // Immediate (non-background) result
      const hasErrors = result.errors.length > 0;
      showSnackbar(
        `Capacity sync complete — ${result.regions_synced}/${result.total_regions} regions` +
        (hasErrors ? ` · ${result.errors.length} error${result.errors.length > 1 ? 's' : ''}` : ''),
        hasErrors ? 'warning' : 'success',
        null,
      );
      await loadCapacityData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to sync capacity', 'error', null);
    } finally {
      if (!unmountedRef.current) {
        setSyncingCapacity(false);
      }
    }
  };

  const handleCancelSync = async () => {
    if (!selectedProviderId) return;
    try {
      await api.cancelProviderCapacitySync(selectedProviderId);
      showSnackbar('Cancellation requested — waiting for running regions to finish…', 'warning');
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to cancel sync', 'error');
    }
  };

  // Poll a region-discovery operation until it reaches a terminal state (or times out).
  // Discovery runs in a worker over Redis, so the result arrives asynchronously.
  const pollRegionSyncOperation = useCallback(async (
    providerId: string,
    operationId: string,
  ): Promise<RegionSyncOperationResponse | null> => {
    const maxAttempts = 60;
    const intervalMs = 2000;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await delay(intervalMs);
      if (unmountedRef.current) return null;
      try {
        const op = await api.getProviderRegionSyncOperation(providerId, operationId);
        if (op.status === 'completed' || op.status === 'failed') {
          return op;
        }
      } catch (error) {
        console.error('Failed polling region sync operation', error);
        return null;
      }
    }
    return null;
  }, []);

  // Apply the region reconciliation the operator has already reviewed and confirmed in the
  // preview dialog (create the new regions, disable the stale ones), then refresh the table.
  // Pure DB reconciliation on the backend from the already-discovered region list.
  const applySyncRegions = async (
    providerId: string,
    operationId: string,
    regions?: string[],
  ) => {
    setSyncingRegions(true);
    try {
      const result = await api.applyProviderRegionSync(providerId, operationId, regions);
      showSnackbar(result.message, 'success', null);
      await loadRegions();
      await loadCapacityData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to apply region sync', 'error', null);
    } finally {
      if (!unmountedRef.current) {
        setSyncingRegions(false);
      }
    }
  };

  // Discover the cloud regions this provider currently offers and reconcile the region table.
  // Discovery is delegated to the workers over Redis: enqueue → poll for the result → show the
  // operator exactly which regions would be added/removed → only write once they confirm.
  const handleSyncRegions = async () => {
    if (!selectedProviderId) {
      showSnackbar('No provider selected', 'warning');
      return;
    }
    const providerId = selectedProviderId;
    setSyncingRegions(true);
    let preview: RegionSyncOperationResponse | null = null;
    try {
      const started = await api.startProviderRegionSync(providerId);
      preview = await pollRegionSyncOperation(providerId, started.operation_id);
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to start region discovery', 'error', null);
      return;
    } finally {
      if (!unmountedRef.current) {
        setSyncingRegions(false);
      }
    }

    if (!preview) {
      showSnackbar('Region discovery timed out — please try again', 'error', null);
      return;
    }
    if (preview.status === 'failed') {
      showSnackbar(preview.error || preview.message || 'Region discovery failed', 'error', null);
      return;
    }
    if (preview.created.length === 0 && preview.disabled.length === 0) {
      showSnackbar(
        `Regions are already up to date — ${preview.available_count} available region(s), no changes`,
        'success',
      );
      return;
    }

    const op = preview;
    // Seed to all discovered regions so a no-interaction confirm still adds them all;
    // RegionAddSelector keeps this ref in sync as the operator ticks/unticks, and
    // onConfirm reads it to send only the chosen subset.
    selectedToAddRef.current = new Set<string>(op.created);
    requestConfirm({
      title: 'Sync regions',
      message: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Syncing regions for {selectedProvider?.name ?? 'this provider'} will apply the
            following changes:
          </Typography>
          {op.created.length > 0 && (
            <Box sx={{ mb: op.disabled.length ? 2 : 0 }}>
              <Typography variant="subtitle2" color="success.main" sx={{ mb: 0.5 }}>
                Add regions ({op.created.length} available — choose which to add)
              </Typography>
              <RegionAddSelector regions={op.created} selectionRef={selectedToAddRef} />
            </Box>
          )}
          {op.disabled.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="error.main">
                Disable {op.disabled.length} region{op.disabled.length === 1 ? '' : 's'} no longer offered
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {op.disabled.join(', ')}
              </Typography>
            </Box>
          )}
        </Box>
      ),
      confirmLabel: 'Apply changes',
      confirmColor: op.disabled.length > 0 ? 'warning' : 'primary',
      onConfirm: () => {
        // Send only the chosen subset (empty selection ⇒ add none, disable-only apply).
        void applySyncRegions(providerId, op.operation_id, Array.from(selectedToAddRef.current));
      },
    });
  };

  // On provider selection, check whether a sync is already in-flight (e.g. user navigated away
  // and came back). If so, resume polling so the button and counter reflect the live state.
  useEffect(() => {
    if (!selectedProviderId) return;
    const cancelledRef = { current: false };

    const resumeIfActive = async () => {
      try {
        const activeJob = await api.getActiveProviderCapacitySyncJob(selectedProviderId);
        if (!activeJob || cancelledRef.current) return;
        setSyncingCapacity(true);
        const processed = activeJob.regions_synced + (activeJob.errors?.length ?? 0);
        if (activeJob.total_regions > 0) {
          setSyncProgress({ synced: processed, total: activeJob.total_regions });
        }
        await pollCapacitySyncJob(selectedProviderId, activeJob.job_id!, cancelledRef);
      } catch {
        // No active job or network error — silently ignore
      }
    };

    resumeIfActive();
    return () => { cancelledRef.current = true; };
  }, [selectedProviderId, pollCapacitySyncJob]);

  useEffect(() => {
    if (regions.length === 0) {
      return;
    }
    setManualLimitDrafts((prev) => {
      const next = { ...prev };
      regions.forEach((region) => {
        const regionId = region.id;
        if (!regionId || manualLimitDirty.has(regionId)) {
          return;
        }
        const capacity = capacityData.get(regionId);
        const manualLimit = capacity?.manual_bot_limit;
        const effectiveLimit = capacity?.effective_instance_limit;
        const displayValue =
          manualLimit === null || manualLimit === undefined
            ? effectiveLimit
            : manualLimit;
        next[regionId] =
          displayValue === null || displayValue === undefined ? '' : String(displayValue);
      });
      return next;
    });
  }, [capacityData, regions, manualLimitDirty]);

  const handleManualLimitChange = (regionId: string, value: string) => {
    setManualLimitDrafts((prev) => ({ ...prev, [regionId]: value }));
    setManualLimitDirty((prev) => {
      const next = new Set(prev);
      next.add(regionId);
      return next;
    });
  };

  const handleManualLimitSave = async (region: Region) => {
    if (!selectedProviderId || !region.id) {
      showSnackbar('No provider or region selected', 'warning');
      return;
    }

    const regionId = region.id;

    if (manualLimitSavingIds.has(regionId)) {
      return;
    }

    if (!manualLimitDirty.has(regionId)) {
      return;
    }

    const rawValue = manualLimitDrafts[regionId] ?? '';
    const trimmed = rawValue.trim();
    const nextValue = trimmed === '' ? null : Number(trimmed);
    if (nextValue !== null && (Number.isNaN(nextValue) || nextValue < 0)) {
      showSnackbar('Manual limit must be a non-negative number', 'warning');
      return;
    }
    if (nextValue !== null) {
      const capacity = capacityData.get(regionId);
      const syncedLimit =
        capacity?.effective_instance_limit ??
        capacity?.instance_limit ??
        null;
      const hasSyncedPositiveLimit =
        typeof syncedLimit === 'number' && Number.isFinite(syncedLimit) && syncedLimit > 0;
      if (hasSyncedPositiveLimit && nextValue > syncedLimit) {
        showSnackbar(
          `Manual limit cannot exceed synced limit (${syncedLimit}).`,
          'warning'
        );
        return;
      }
    }

    setManualLimitSavingIds((prev) => {
      const next = new Set(prev);
      next.add(regionId);
      return next;
    });
    try {
      await api.updateRegionManualLimit(selectedProviderId, regionId, nextValue);
      await loadCapacityData();
      showSnackbar('Manual limit updated', 'success');
      setManualLimitDirty((prev) => {
        const next = new Set(prev);
        next.delete(regionId);
        return next;
      });
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to update manual limit', 'error');
    } finally {
      setManualLimitSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(regionId);
        return next;
      });
    }
  };

  const pollReservationOperation = useCallback(async (
    providerId: string,
    regionId: string,
    operationId: string,
    actionLabel: string
  ) => {
    const maxAttempts = 60;
    const intervalMs = 2000;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await delay(intervalMs);
      try {
        const operation = await api.getAzureReservationOperation(providerId, regionId, operationId);
        const completed = Number(operation.result?.completed_count);
        const total = Number(
          operation.requested_count ?? operation.result?.requested_count
        );
        if (Number.isFinite(completed) && Number.isFinite(total) && total > 0) {
          setRegionProgress(regionId, {
            phase: operation.operation_type,
            completed,
            total,
          });
        }
        if (operation.status === 'completed') {
          setRegionProgress(regionId, null);
          // Reserve / release / scale-down all change the reservation pool, and the
          // capacity "used" bars are derived from that pool — refresh both.
          await Promise.all([loadAzureReservations(), loadCapacityData()]);
          showSnackbar(`${actionLabel} completed`, 'success');
          return;
        }
        if (operation.status === 'failed') {
          setRegionProgress(regionId, null);
          await Promise.all([loadAzureReservations(), loadCapacityData()]);
          showSnackbar(operation.error || `${actionLabel} failed`, 'error');
          return;
        }
      } catch (error) {
        console.error(`Failed polling reservation operation ${operationId}`, error);
        setRegionProgress(regionId, null);
        return;
      }
    }
    setRegionProgress(regionId, null);
    // Poll gave up — refresh from the API so the cell reflects the latest DB state instead of
    // staying on the last progress frame if the operation actually finished meanwhile.
    await Promise.all([loadAzureReservations(), loadCapacityData()]);
    showSnackbar(`${actionLabel} is still running in background`, 'warning');
  }, [loadAzureReservations, loadCapacityData, showSnackbar, setRegionProgress]);

  // Single entry point for polling a reservation operation. Dedupes by operation id
  // and owns the region's busy flag for the whole life of the poll, so both the
  // triggering handler and the load-time resume effect can call it safely.
  const ensureReservationPolling = useCallback((
    providerId: string,
    regionId: string,
    operationId: string,
    actionLabel: string,
  ) => {
    if (activeReservationPollsRef.current.has(operationId)) return;
    activeReservationPollsRef.current.add(operationId);
    setReservationBusy(regionId, true);
    void pollReservationOperation(providerId, regionId, operationId, actionLabel).finally(() => {
      activeReservationPollsRef.current.delete(operationId);
      setReservationBusy(regionId, false);
    });
  }, [pollReservationOperation, setReservationBusy]);

  // Bulk-reserve group poller: polls the whole set of per-region operations with ONE request per
  // tick (instead of one poller per region), applying each region's progress/terminal transition and
  // refreshing the reservation pool + capacity bars at most once per tick as regions settle.
  const pollReservationOperationGroup = useCallback(async (
    providerId: string,
    entries: { regionId: string; operationId: string }[],
    actionLabel: string,
  ) => {
    const maxAttempts = 90;   // ~3 min at 2s — a UI cap; units persist incrementally server-side
    const intervalMs = 2000;
    const pending = new Map<string, string>(entries.map((e) => [e.operationId, e.regionId]));
    entries.forEach((e) => {
      activeReservationPollsRef.current.add(e.operationId);
      setReservationBusy(e.regionId, true);
    });
    let completedCount = 0;
    let failedCount = 0;
    const settle = (operationId: string, regionId: string) => {
      pending.delete(operationId);
      activeReservationPollsRef.current.delete(operationId);
      setRegionProgress(regionId, null);
      setReservationBusy(regionId, false);
    };
    try {
      for (let attempt = 0; attempt < maxAttempts && pending.size > 0; attempt += 1) {
        await delay(intervalMs);
        let operations;
        try {
          operations = await api.getProviderReservationOperationsBatch(providerId, Array.from(pending.keys()));
        } catch (error) {
          console.error('Failed polling reservation operation group', error);
          continue;  // transient; retry next tick
        }
        let anyTerminal = false;
        for (const operation of operations) {
          const regionId = pending.get(operation.operation_id);
          if (!regionId) continue;
          const completed = Number(operation.result?.completed_count);
          const total = Number(operation.requested_count ?? operation.result?.requested_count);
          if (Number.isFinite(completed) && Number.isFinite(total) && total > 0) {
            setRegionProgress(regionId, { phase: operation.operation_type, completed, total });
          }
          if (operation.status === 'completed') {
            completedCount += 1;
            settle(operation.operation_id, regionId);
            anyTerminal = true;
          } else if (operation.status === 'failed') {
            failedCount += 1;
            settle(operation.operation_id, regionId);
            anyTerminal = true;
          }
        }
        if (anyTerminal) {
          // Pool + capacity "used" bars changed; refresh once for the tick, not once per region.
          await Promise.all([loadAzureReservations(), loadCapacityData()]);
        }
      }
    } finally {
      // Anything still pending after the cap: release its busy flag and resync from the DB so the
      // cells reflect real state instead of the last progress frame.
      if (pending.size > 0) {
        Array.from(pending.entries()).forEach(([operationId, regionId]) => settle(operationId, regionId));
        await Promise.all([loadAzureReservations(), loadCapacityData()]);
      }
    }
    if (completedCount > 0 && failedCount === 0) {
      showSnackbar(`${actionLabel} completed for ${completedCount} region${completedCount === 1 ? '' : 's'}`, 'success');
    } else if (completedCount > 0 && failedCount > 0) {
      showSnackbar(`${actionLabel}: ${completedCount} completed · ${failedCount} failed`, 'warning');
    } else if (failedCount > 0) {
      showSnackbar(`${actionLabel} failed for ${failedCount} region${failedCount === 1 ? '' : 's'}`, 'error');
    }
  }, [loadAzureReservations, loadCapacityData, showSnackbar, setRegionProgress, setReservationBusy]);

  const handleReserveReservations = async (region: Region, count: number, includeIpv6: boolean) => {
    if (!selectedProviderId || !region.id) return;
    if (!Number.isFinite(count) || count <= 0) {
      showSnackbar('Reservation count must be a positive number', 'warning');
      return;
    }
    const regionId = region.id;
    setReservationBusy(regionId, true);
    try {
      const summary = await api.reserveAzureReservations(selectedProviderId, regionId, count, includeIpv6);
      setAzureReservations((prev) => new Map(prev).set(regionId, summary));
      if (summary.operation_id) {
        setRegionProgress(regionId, { phase: 'reserve', completed: 0, total: count });
        showSnackbar(`Reservation operation queued for ${region.region_code}`, 'success');
        ensureReservationPolling(selectedProviderId, regionId, summary.operation_id, 'Reservation');
      } else {
        showSnackbar(`Reserved ${count} Azure network slots for ${region.region_code}`, 'success');
        setReservationBusy(regionId, false);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to reserve Azure capacity', 'error');
      setReservationBusy(regionId, false);
    }
  };

  const handleScaleDownReservations = async (region: Region, count: number) => {
    if (!selectedProviderId || !region.id) return;
    if (!Number.isFinite(count) || count <= 0) {
      showSnackbar('Scale down count must be a positive number', 'warning');
      return;
    }
    const regionId = region.id;
    setReservationBusy(regionId, true);
    try {
      const summary = await api.scaleDownAzureReservations(selectedProviderId, regionId, count);
      setAzureReservations((prev) => new Map(prev).set(regionId, summary));
      if (summary.operation_id) {
        setRegionProgress(regionId, { phase: 'scale_down', completed: 0, total: count });
        showSnackbar('Recycle (reboot) operation queued', 'success');
        ensureReservationPolling(selectedProviderId, regionId, summary.operation_id, 'Recycle');
      } else {
        showSnackbar(`Recycled ${count} free reservation(s)`, 'success');
        setReservationBusy(regionId, false);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to scale down reservations', 'error');
      setReservationBusy(regionId, false);
    }
  };

  const handleReleaseReservations = async (region: Region, count?: number) => {
    if (!selectedProviderId || !region.id) return;
    if (count !== undefined && (!Number.isFinite(count) || count <= 0)) {
      showSnackbar('Release count must be a positive number', 'warning');
      return;
    }
    const regionId = region.id;
    setReservationBusy(regionId, true);
    try {
      const summary = await api.releaseAzureReservations(selectedProviderId, regionId, count);
      setAzureReservations((prev) => new Map(prev).set(regionId, summary));
      if (summary.operation_id) {
        setRegionProgress(regionId, {
          phase: 'release',
          completed: 0,
          total: count ?? summary.free,
        });
        showSnackbar('Release operation queued', 'success');
        ensureReservationPolling(selectedProviderId, regionId, summary.operation_id, 'Release');
      } else {
        showSnackbar('Released free reservation(s)', 'success');
        setReservationBusy(regionId, false);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to release reservations', 'error');
      setReservationBusy(regionId, false);
    }
  };

  // Release a specific set of free reservation slots the operator checked in the table.
  // Flows through the same release endpoint as count/all release, passing reservation_ids.
  const handleReleaseSelectedReservations = async (region: Region, ids: string[]) => {
    if (!selectedProviderId || !region.id || ids.length === 0) return;
    const regionId = region.id;
    setReservationBusy(regionId, true);
    try {
      const summary = await api.releaseAzureReservations(selectedProviderId, regionId, undefined, ids);
      setAzureReservations((prev) => new Map(prev).set(regionId, summary));
      if (summary.operation_id) {
        setRegionProgress(regionId, {
          phase: 'release',
          completed: 0,
          total: ids.length,
        });
        showSnackbar('Release operation queued', 'success');
        ensureReservationPolling(selectedProviderId, regionId, summary.operation_id, 'Release');
      } else {
        showSnackbar('Released selected reservation(s)', 'success');
        setReservationBusy(regionId, false);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to release reservations', 'error');
      setReservationBusy(regionId, false);
    }
  };

  // Reclaim reservations stuck "in use" whose claiming bot was terminated/deleted (their Azure
  // resources are already gone). Normal Release only clears "free" units, so these need this path.
  const handleReclaimReservations = async (region: Region) => {
    if (!selectedProviderId || !region.id) return;
    const regionId = region.id;
    setReservationBusy(regionId, true);
    try {
      const summary = await api.reclaimAzureReservations(selectedProviderId, regionId);
      setAzureReservations((prev) => new Map(prev).set(regionId, summary));
      if (summary.operation_id) {
        showSnackbar('Reclaim operation queued', 'success');
        ensureReservationPolling(selectedProviderId, regionId, summary.operation_id, 'Reclaim');
      } else {
        showSnackbar(summary.message || 'No orphaned reservations to reclaim', 'warning');
        setReservationBusy(regionId, false);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to reclaim reservations', 'error');
      setReservationBusy(regionId, false);
    }
  };

  // Release every free reservation across a set of regions by fanning out to the per-region
  // release endpoint (there's no provider-wide release endpoint). Only regions that actually
  // have free reservations are hit — regions with none are skipped so the button can't hang
  // waiting on a no-op operation. Shared by "Release All Reservations" (all regions) and the
  // "Release Reservations" bulk action (checkbox-selected regions).
  const releaseFreeReservationsForRegions = async (targetRegions: Region[]) => {
    if (!selectedProviderId) {
      showSnackbar('No provider selected', 'warning');
      return;
    }
    const regionsWithFree = targetRegions.filter((region) => {
      const summary = region.id ? azureReservations.get(region.id) : undefined;
      return Boolean(region.id) && summary !== undefined && summary.free > 0;
    });
    if (regionsWithFree.length === 0) {
      showSnackbar('No free reservations to release for the selected region(s)', 'warning');
      return;
    }
    setReleasingReservations(true);
    let queued = 0;
    let failed = 0;
    try {
      for (const region of regionsWithFree) {
        const regionId = region.id!;
        const freeBefore = azureReservations.get(regionId)?.free ?? 0;
        setReservationBusy(regionId, true);
        try {
          const summary = await api.releaseAzureReservations(selectedProviderId, regionId);
          setAzureReservations((prev) => new Map(prev).set(regionId, summary));
          if (summary.operation_id) {
            setRegionProgress(regionId, { phase: 'release', completed: 0, total: freeBefore });
            ensureReservationPolling(selectedProviderId, regionId, summary.operation_id, 'Release');
            queued += 1;
          } else {
            // Nothing was queued for this region — clear its busy flag ourselves
            // (ensureReservationPolling owns it only when an operation is polling).
            setReservationBusy(regionId, false);
          }
        } catch (error) {
          failed += 1;
          setReservationBusy(regionId, false);
        }
      }
      if (queued > 0) {
        showSnackbar(
          `Release queued for ${queued} region${queued === 1 ? '' : 's'}` +
            (failed > 0 ? ` · ${failed} failed` : ''),
          failed > 0 ? 'warning' : 'success',
        );
      } else {
        showSnackbar('Failed to queue any reservation releases', 'error');
      }
    } finally {
      if (!unmountedRef.current) {
        setReleasingReservations(false);
      }
    }
  };

  // Release all free reservations across every region of the provider.
  const handleReleaseAllReservations = () => releaseFreeReservationsForRegions(regions);

  // Release all free reservations across the checkbox-selected regions (bulk-action bar).
  const handleReleaseSelectedRegionsReservations = () =>
    releaseFreeReservationsForRegions(regions.filter((r) => r.id && selectedRegions.has(r.id)));

  // Split a total slot count as evenly as possible across `regionCount` regions.
  // Any remainder is spread one-per-region across the first regions, so e.g.
  // 10 across 3 regions → [4, 3, 3] and the parts always sum back to the total.
  const splitReservationCount = (total: number, regionCount: number): number[] => {
    if (regionCount <= 0) return [];
    const base = Math.floor(total / regionCount);
    const remainder = total % regionCount;
    return Array.from({ length: regionCount }, (_, i) => base + (i < remainder ? 1 : 0));
  };

  // The regions the bulk-reserve dialog will act on: the current selection, in
  // table order, restricted to regions with a real id.
  const bulkReserveRegions = useMemo(
    () => filteredRegions.filter((r) => r.id && selectedRegions.has(r.id)),
    [filteredRegions, selectedRegions],
  );

  // Remaining reservable slots for a region: the app's canonical "how many more
  // bots fit" number, which already folds in the instance, vCPU, IP and manual
  // limits. Returns null when the region has no synced capacity data.
  const availableCapacityForRegion = useCallback(
    (regionId?: string): number | null => {
      if (!regionId) return null;
      const capacity = capacityData.get(regionId);
      if (!capacity) return null;
      const available = capacity.effective_instance_available;
      return typeof available === 'number' ? Math.max(0, available) : null;
    },
    [capacityData],
  );

  // Sum of available capacity across the selected regions — the ceiling the
  // "Fill max available" option targets. Regions without synced capacity add 0.
  const bulkReserveMaxAvailable = useMemo(
    () =>
      bulkReserveRegions.reduce(
        (sum, region) => sum + (availableCapacityForRegion(region.id) ?? 0),
        0,
      ),
    [bulkReserveRegions, availableCapacityForRegion],
  );

  // Number of selected regions we can't size because their capacity isn't synced.
  const bulkReserveUnsyncedCount = useMemo(
    () =>
      bulkReserveRegions.filter((region) => availableCapacityForRegion(region.id) === null)
        .length,
    [bulkReserveRegions, availableCapacityForRegion],
  );

  // Per-region allocation preview for the dialog. In max mode each region gets
  // its own available capacity; otherwise the total is divided equally. Empty
  // when the input is not a positive integer or nothing is selected.
  const bulkReservePlan = useMemo(() => {
    if (bulkReserveRegions.length === 0) {
      return [] as { region: Region; count: number }[];
    }
    if (bulkReserveMaxMode) {
      return bulkReserveRegions.map((region) => ({
        region,
        count: availableCapacityForRegion(region.id) ?? 0,
      }));
    }
    const total = Number(bulkReserveTotal);
    if (!Number.isInteger(total) || total <= 0) {
      return [] as { region: Region; count: number }[];
    }
    const counts = splitReservationCount(total, bulkReserveRegions.length);
    return bulkReserveRegions.map((region, i) => ({ region, count: counts[i] }));
  }, [bulkReserveMaxMode, bulkReserveTotal, bulkReserveRegions, availableCapacityForRegion]);

  // Reserve capacity across every selected region in one action, dividing the
  // requested total equally. Regions whose share rounds to 0 (total < region
  // count) are skipped. Each reserve is a background job — the region rows own
  // the busy flag, progress bar and operation polling — so we dismiss the dialog
  // immediately and let the reservations run in the background (like single-region reserve).
  // The whole selection is enqueued in ONE batch request (not N sequential per-region calls) and
  // tracked by ONE group poller (not N per-region pollers), so "reserve all regions" stays light on
  // the backend's synchronous request path. Each region still becomes its own operation server-side.
  const runBulkReserveJobs = async (
    providerId: string,
    plan: { region: Region; count: number }[],
    includeIpv6: boolean,
  ) => {
    const items = plan.map(({ region, count }) => ({
      region_id: region.id!,
      count,
      include_ipv6: includeIpv6,
    }));
    plan.forEach(({ region }) => setReservationBusy(region.id!, true));

    const response = await api.reserveProviderReservationsBatch(providerId, items).catch((error: any) => {
      plan.forEach(({ region }) => setReservationBusy(region.id!, false));
      showSnackbar(error.response?.data?.detail || 'Failed to queue reservations', 'error');
      return null;
    });
    if (!response) return;

    const countByRegion = new Map(plan.map(({ region, count }) => [region.id!, count]));
    const entries: { regionId: string; operationId: string }[] = [];
    let failed = 0;
    for (const result of response.results) {
      if (result.operation_id) {
        setRegionProgress(result.region_id, {
          phase: 'reserve',
          completed: 0,
          total: countByRegion.get(result.region_id) ?? 0,
        });
        entries.push({ regionId: result.region_id, operationId: result.operation_id });
      } else {
        failed += 1;
        setReservationBusy(result.region_id, false);
      }
    }

    if (entries.length > 0) {
      showSnackbar(
        `Reservation queued for ${entries.length} region${entries.length === 1 ? '' : 's'}` +
          (failed > 0 ? ` · ${failed} failed` : ''),
        failed > 0 ? 'warning' : 'success',
      );
      void pollReservationOperationGroup(providerId, entries, 'Reservation');
    } else {
      showSnackbar('Failed to queue any reservations', 'error');
    }
  };

  const handleBulkReserve = () => {
    if (!selectedProviderId) {
      showSnackbar('No provider selected', 'warning');
      return;
    }
    const plan = bulkReservePlan.filter((entry) => entry.count > 0);
    if (plan.length === 0) {
      showSnackbar('Enter a total of at least one slot per selected region', 'warning');
      return;
    }
    const providerId = selectedProviderId;
    const includeIpv6 = bulkReserveIncludeIpv6;
    // Dismiss the dialog up front; the reservations proceed as background jobs.
    setBulkReserveDialogOpen(false);
    setBulkReserveTotal('');
    setBulkReserveIncludeIpv6(false);
    setBulkReserveMaxMode(false);
    void runBulkReserveJobs(providerId, plan, includeIpv6);
  };

  // Request a +50% quota increase (vCPUs, public IPs, instance count) for the
  // region from the cloud provider. The provider raises the limit automatically
  // where possible or opens a support ticket on its side.
  const QUOTA_INCREASE_MULTIPLIER = 1.5;
  const dimensionLabel = (dimension: string): string => {
    switch (dimension) {
      case 'vcpu': return 'vCPUs';
      case 'ip': return 'Public IPs';
      case 'instance': return 'Instances';
      default: return dimension;
    }
  };

  const handleIncreaseQuota = async (region: Region) => {
    if (!selectedProviderId || !region.id) return;
    const providerType = selectedProvider?.provider_type;
    if (providerType !== 'azure' && providerType !== 'gcp') {
      showSnackbar('Quota increase is only supported for Azure and GCP', 'warning');
      return;
    }

    setQuotaIncreaseIds((prev) => new Set(prev).add(region.id!));
    try {
      const result = await api.requestRegionQuotaIncrease(
        selectedProviderId,
        region.id,
        QUOTA_INCREASE_MULTIPLIER
      );
      const items: QuotaIncreaseItem[] = result.requests || [];
      if (items.length === 0) {
        showSnackbar(`No quota dimensions were eligible for ${region.region_code}`, 'warning');
        return;
      }
      const failed = items.filter((i) => i.status === 'failed').length;
      const skipped = items.filter((i) => i.status === 'skipped').length;
      const ok = items.length - failed - skipped;
      const detail = items
        .map((i) => `${dimensionLabel(i.dimension)} ${i.current_limit}→${i.requested_limit} (${i.status})`)
        .join('; ');
      const message = `Quota increase for ${region.region_code}: ${detail}`;
      if (failed > 0 && ok === 0) {
        showSnackbar(message, 'error', 10000);
      } else if (failed > 0 || skipped > 0) {
        showSnackbar(message, 'warning', 10000);
      } else {
        showSnackbar(message, 'success', 8000);
      }
      // Auto-approved increases change the synced limits — refresh capacity.
      await loadCapacityData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Failed to request quota increase', 'error');
    } finally {
      setQuotaIncreaseIds((prev) => {
        const next = new Set(prev);
        next.delete(region.id!);
        return next;
      });
    }
  };

  // Command generation functions
  const generateImageIdCommand = (providerType: string, regionCode: string, architecture: string) => {
    const arch = architecture === 'amd64' ? 'amd64' : 'arm64';
    
    switch (providerType.toLowerCase()) {
      case 'aws':
        return `aws ssm get-parameters --region ${regionCode} \\
  --names "/aws/service/canonical/ubuntu/server/24.04/stable/current/${arch}/hvm/ebs-gp3/ami-id" \\
  --query "Parameters[0].Value" --output text`;
      
      case 'gcp':
        return `gcloud compute images list --filter='family="ubuntu-2404-lts" AND architecture="${arch === 'amd64' ? 'X86_64' : 'ARM64'}"' --format="value(name)" --limit=1`;
      
      case 'azure':
        return `az vm image list --publisher Canonical --offer 0001-com-ubuntu-server-jammy --sku 24_04-lts-gen2 --location ${regionCode} --query "[0].name" --output tsv`;
      
  default:
    return `# Command for ${providerType} provider not available`;
  }
};

  const copyImageCommand = (region: Region, architecture: 'amd64' | 'arm64') => {
    const selectedProvider = providers.find(p => p.id === selectedProviderId);
    if (!selectedProvider) {
      showSnackbar('Select a provider first', 'warning');
      return;
    }
    const command = generateImageIdCommand(selectedProvider.provider_type, region.region_code, architecture);
    copyToClipboard(command);
  };

  const handleInlineEditSave = async () => {
    if (!inlineEdit) {
      return;
    }
    if (!selectedProviderId) {
      showSnackbar('Select a provider first', 'warning');
      return;
    }
    setInlineSaving(true);
    try {
      const trimmedValue = inlineEdit.value.trim();
      const updateData: RegionUpdateRequest =
        inlineEdit.field === 'image_id_amd64'
          ? { image_id_amd64: trimmedValue }
          : { image_id_arm64: trimmedValue };

      await api.updateRegion(selectedProviderId, inlineEdit.regionId, updateData);
      const architectureLabel = inlineEdit.field === 'image_id_amd64' ? 'AMD64' : 'ARM64';
      showSnackbar(`${architectureLabel} image ID updated`, 'success');
      setInlineEdit(null);
      await loadRegions();
    } catch (error) {
      console.error('Error updating image ID inline:', error);
      showSnackbar('Failed to update image ID', 'error');
    } finally {
      setInlineSaving(false);
    }
  };

  const startInlineEdit = (region: Region, field: 'image_id_amd64' | 'image_id_arm64') => {
    if (!region.id) {
      showSnackbar('Invalid region ID', 'error');
      return;
    }
    if (inlineSaving) {
      return;
    }
    const currentValue = field === 'image_id_amd64'
      ? region.image_id_amd64 || ''
      : region.image_id_arm64 || '';
    setInlineEdit({
      regionId: region.id,
      field,
      value: currentValue
    });
  };

  const handleInlineEditChange = (value: string) => {
    setInlineEdit((prev) => (prev ? { ...prev, value } : prev));
  };

  const cancelInlineEdit = () => {
    if (!inlineSaving) {
      setInlineEdit(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar('Command copied to clipboard!', 'success');
    } catch (err) {
      showSnackbar('Failed to copy to clipboard', 'error');
    }
  };

  const handleAutoFillRegion = () => {
    if (!createForm.region_code) return;

    const regionCode = createForm.region_code;
    
    setCreateForm({
      ...createForm,
      name: regionCode
    });
  };

  const handleBulkCreateRegions = async () => {
    if (!bulkRegionsText.trim()) {
      showSnackbar('Please enter region codes', 'warning');
      return;
    }

    const regionCodes = bulkRegionsText
      .split('\n')
      .map(line => line.trim())
      .map(line => line.replace(/[^a-z0-9-]/g, '')) // Strip non-lowercase alphanumeric or dash characters
      .filter(line => line.length > 0);

    if (regionCodes.length === 0) {
      showSnackbar('No valid region codes found', 'warning');
      return;
    }

    const selectedProvider = providers.find(p => p.id === selectedProviderId);
    if (!selectedProvider) {
      showSnackbar('No provider selected', 'error');
      return;
    }

    setLoading(true);
    const counts = { successCount: 0, errorCount: 0 };

    try {
      for (let i = 0; i < regionCodes.length; i += BULK_BATCH_SIZE) {
        const batch = regionCodes.slice(i, i + BULK_BATCH_SIZE);
        await Promise.all(batch.map(async (regionCode) => {
          try {
            const regionData: RegionCreateRequest = {
              name: regionCode,
              region_code: regionCode,
              location: '',
              continent: '',
              configured: false,
              image_id_amd64: '',
              image_id_arm64: ''
            };

            await api.createRegion(selectedProviderId, regionData);
            counts.successCount++;
          } catch (error) {
            console.error(`Failed to create region ${regionCode}:`, error);
            counts.errorCount++;
          }
        }));
        if (i + batch.length < regionCodes.length) {
          await delay(BATCH_DELAY_MS);
        }
      }

      if (counts.successCount > 0) {
        showSnackbar(`Successfully created ${counts.successCount} regions${counts.errorCount > 0 ? `, ${counts.errorCount} failed` : ''}`, 
          counts.errorCount > 0 ? 'warning' : 'success');
        loadRegions();
      } else {
        showSnackbar('Failed to create any regions', 'error');
      }
    } finally {
      setLoading(false);
      setBulkCreateDialogOpen(false);
      setBulkRegionsText('');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRegions.size === 0) {
      showSnackbar('No regions selected', 'warning');
      return;
    }

    setLoading(true);
    const counts = { successCount: 0, errorCount: 0 };

    try {
      const regionIds = Array.from(selectedRegions);
      for (let i = 0; i < regionIds.length; i += BULK_BATCH_SIZE) {
        const batch = regionIds.slice(i, i + BULK_BATCH_SIZE);
        await Promise.all(batch.map(async (regionId) => {
          try {
            await api.deleteRegion(selectedProviderId, regionId);
            counts.successCount++;
          } catch (error: any) {
            const errorMessage = error?.response?.data?.detail || `Failed to delete region ${regionId}`;
            console.error(`Failed to delete region ${regionId}:`, error);
            showSnackbar(errorMessage, 'error');
            counts.errorCount++;
          }
        }));
        if (i + batch.length < regionIds.length) {
          await delay(BATCH_DELAY_MS);
        }
      }

      if (counts.successCount > 0) {
        showSnackbar(`Successfully deleted ${counts.successCount} regions${counts.errorCount > 0 ? `, ${counts.errorCount} failed` : ''}`, 
          counts.errorCount > 0 ? 'warning' : 'success');
        setSelectedRegions(new Set());
        loadRegions();
      } else {
        showSnackbar('Failed to delete any regions', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkToggleConfigured = async () => {
    if (selectedRegions.size === 0) {
      showSnackbar('No regions selected', 'warning');
      return;
    }

    const selectedRegionObjects = regions.filter(r => r.id && selectedRegions.has(r.id));
    const allEnabled = selectedRegionObjects.every(r => r.configured);
    const action = allEnabled ? 'disable' : 'enable';

    setLoading(true);
    const counts = { successCount: 0, errorCount: 0 };

    try {
      const regionIds = Array.from(selectedRegions);
      for (let i = 0; i < regionIds.length; i += BULK_BATCH_SIZE) {
        const batch = regionIds.slice(i, i + BULK_BATCH_SIZE);
        await Promise.all(batch.map(async (regionId) => {
          try {
            const requestData = { configured: !allEnabled };
            await api.updateRegion(selectedProviderId, regionId, requestData);
            counts.successCount++;
          } catch (error) {
            console.error(`Failed to update region ${regionId}:`, error);
            counts.errorCount++;
          }
        }));
        if (i + batch.length < regionIds.length) {
          await delay(BATCH_DELAY_MS);
        }
      }

      if (counts.successCount > 0) {
        showSnackbar(`Successfully ${action}d ${counts.successCount} regions${counts.errorCount > 0 ? `, ${counts.errorCount} failed` : ''}`, 
          counts.errorCount > 0 ? 'warning' : 'success');
        loadRegions();
      } else {
        showSnackbar(`Failed to ${action} any regions`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadProviders = useCallback(async () => {
    try {
      // UI-only gating: drop providers whose type is disabled in the UI so they
      // don't appear in the selector or get auto-selected.
      const data = filterUiSupportedProviders(
        await api.getProviders(),
        (p) => p.provider_type
      );
      setProviders(data);
      if (data.length > 0) {
        // Keep current selection only if it exists in the loaded list; otherwise select first provider or ''
        setSelectedProviderId((prev) => {
          if (!prev) return data[0].id;
          if (data.some((p) => p.id === prev)) return prev;
          return data[0].id;
        });
      } else {
        setSelectedProviderId('');
      }
    } catch (error: any) {
      console.error('Error loading providers:', error);
      showSnackbar('Failed to load providers', 'error');
    }
  }, [showSnackbar]);

  const loadRegions = useCallback(async () => {
    if (!selectedProviderId) return;
    
    setLoading(true);
    try {
      console.log('Loading regions for provider:', selectedProviderId);
      const data = await api.getProviderRegions(selectedProviderId);
      console.log('Loaded regions:', data);
      setRegions(data);
    } catch (error: any) {
      console.error('Error loading regions:', error);
      showSnackbar('Failed to load regions', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedProviderId, showSnackbar]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // Clear stale data immediately when the selected provider changes so the
  // region filter never operates on a previous provider's regions.
  useEffect(() => {
    setRegions([]);
    setSelectedRegions(new Set());
    setCapacityData(new Map());
    setAzureReservations(new Map());
    setManualLimitDrafts({});
    setManualLimitDirty(new Set());
    setExpandedRegions(new Set());
    setReservationBusyIds(new Set());
    setReservationProgress(new Map());
  }, [selectedProviderId]);

  useEffect(() => {
    if (selectedProviderId) {
      loadRegions();
      loadCapacityData();
    }
  }, [selectedProviderId, loadRegions, loadCapacityData]);

  useEffect(() => {
    loadAzureReservations();
  }, [loadAzureReservations]);

  // Resume polling for any region whose summary reports an in-flight reservation
  // operation — covers page reloads and operations started by another operator, so
  // the action buttons stay disabled and progress keeps updating. Deduped by op id.
  useEffect(() => {
    if (!selectedProviderId) return;
    azureReservations.forEach((summary, regionId) => {
      const status = summary.operation_status;
      const operationId = summary.operation_id;
      if (!operationId || (status !== 'queued' && status !== 'in_progress')) return;
      ensureReservationPolling(selectedProviderId, regionId, operationId, 'Reservation');
    });
  }, [azureReservations, selectedProviderId, ensureReservationPolling]);

  // Sync prop → local state ONLY when the prop itself changes from outside.
  // Intentionally excludes selectedProviderId from deps so a user's local
  // dropdown selection is never overwritten by a stale prop comparison.
  useEffect(() => {
    if (propSelectedProviderId && propSelectedProviderId !== prevPropProviderIdRef.current) {
      prevPropProviderIdRef.current = propSelectedProviderId;
      setSelectedProviderId(propSelectedProviderId);
    }
  }, [propSelectedProviderId]);

  const handleSelectAll = () => {
    if (selectedRegions.size === filteredRegions.length && filteredRegions.length > 0) {
      setSelectedRegions(new Set());
    } else {
      setSelectedRegions(new Set(filteredRegions.map(r => r.id).filter((id): id is string => id !== undefined)));
    }
  };

  const handleSelectRegion = (regionId: string | undefined) => {
    if (!regionId) return;
    const newSelected = new Set(selectedRegions);
    if (newSelected.has(regionId)) {
      newSelected.delete(regionId);
    } else {
      newSelected.add(regionId);
    }
    setSelectedRegions(newSelected);
  };

  const handleCreateRegion = async () => {
    try {
      const response = await api.createRegion(selectedProviderId, createForm);
      showSnackbar(`Region "${response.name}" created successfully`, 'success');
      setCreateDialogOpen(false);
      setCreateForm({
        name: '',
        region_code: '',
        location: '',
        continent: '',
        configured: false
      });
      loadRegions();
    } catch (error: any) {
      showSnackbar('Failed to create region', 'error');
      console.error('Error creating region:', error);
    }
  };

  const handleEditRegion = async () => {
    if (!editingRegion) return;
    
    try {
      if (!editingRegion.id) {
        showSnackbar('Invalid region ID', 'error');
        return;
      }
      const response = await api.updateRegion(selectedProviderId, editingRegion.id, editForm);
      showSnackbar(`Region "${response.name}" updated successfully`, 'success');
      setEditDialogOpen(false);
      setEditingRegion(null);
      setEditForm({});
      loadRegions();
    } catch (error: any) {
      showSnackbar('Failed to update region', 'error');
      console.error('Error updating region:', error);
    }
  };

  const handleDeleteRegion = async (region: Region) => {
    try {
      if (!region.id) {
        showSnackbar('Invalid region ID', 'error');
        return;
      }
      await api.deleteRegion(selectedProviderId, region.id);
      showSnackbar(`Region "${region.name}" deleted successfully`, 'success');
      loadRegions();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Failed to delete region';
      showSnackbar(errorMessage, 'error');
      console.error('Error deleting region:', error);
    }
  };

  const handleToggleConfigured = async (region: Region) => {
    try {
      if (!region.id) {
        showSnackbar('Invalid region ID', 'error');
        return;
      }
      const requestData = { configured: !region.configured };
      console.log('Sending update request:', {
        providerId: selectedProviderId,
        regionId: region.id,
        requestData
      });
      await api.updateRegion(selectedProviderId, region.id, requestData);
      showSnackbar(`Region "${region.name}" ${!region.configured ? 'enabled' : 'disabled'}`, 'success');
      loadRegions();
    } catch (error: any) {
      showSnackbar('Failed to update region', 'error');
      console.error('Error updating region:', error);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderImageIdCell = (
    region: Region,
    field: 'image_id_amd64' | 'image_id_arm64',
    architecture: 'amd64' | 'arm64'
  ) => {
    const isEditing =
      inlineEdit !== null &&
      inlineEdit.regionId === region.id &&
      inlineEdit.field === field;
    const currentValue =
      field === 'image_id_amd64' ? region.image_id_amd64 : region.image_id_arm64;

    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={inlineEdit?.value ?? ''}
            onChange={(e) => handleInlineEditChange(e.target.value)}
            size="small"
            variant="standard"
            autoFocus
            disabled={inlineSaving}
            sx={{ minWidth: 200 }}
            placeholder={architecture === 'amd64' ? 'ami-1234' : 'ami-arm64'}
          />
          <Tooltip title="Save">
            <span>
              <IconButton
                size="small"
                onClick={handleInlineEditSave}
                disabled={inlineSaving}
                color="primary"
              >
                {inlineSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <span>
              <IconButton size="small" onClick={cancelInlineEdit} disabled={inlineSaving}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {currentValue || 'N/A'}
        </Typography>
        <Tooltip title={`Copy command to get ${architecture.toUpperCase()} image ID`}>
          <span>
            <IconButton
              size="small"
              onClick={() => copyImageCommand(region, architecture)}
              disabled={!selectedProviderId || inlineSaving}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={`Edit ${architecture.toUpperCase()} image ID`}>
          <span>
            <IconButton
              size="small"
              onClick={() => startInlineEdit(region, field)}
              disabled={!region.id || inlineSaving}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  };

  const refreshRegionReservations = useCallback(async (region: Region) => {
    if (!selectedProviderId || !region.id) return;
    const regionId = region.id;
    try {
      const summary = await api.listAzureReservations(selectedProviderId, regionId);
      setAzureReservations((prev) => new Map(prev).set(regionId, summary));
    } catch (error) {
      console.error('Failed to refresh reservations', error);
    }
  }, [selectedProviderId]);

  const reservationsSupported =
    selectedProvider?.provider_type === 'azure' || selectedProvider?.provider_type === 'gcp';

  const renderStatusChip = (region: Region) => {
    const capacity = capacityData.get(region.id || '');
    if (!capacity) {
      return <Chip label="Not synced" size="small" variant="outlined" />;
    }
    const statusColors = { green: 'success', yellow: 'warning', red: 'error' } as const;
    const statusLabels = { green: 'Available', yellow: 'Cooldown', red: 'Unavailable' } as const;
    const statusKey = capacity.status as keyof typeof statusLabels;
    const statusLabel = statusLabels[statusKey] ?? String(capacity.status);
    const chip = <Chip label={statusLabel} color={statusColors[statusKey] as any} size="small" />;
    if (capacity.cooldown_reason || capacity.cooldown_until) {
      const until = capacity.cooldown_until ? new Date(capacity.cooldown_until).toLocaleString() : null;
      return (
        <Tooltip title={`${capacity.cooldown_reason || 'In cooldown'}${until ? ` · until ${until}` : ''}`}>
          {chip}
        </Tooltip>
      );
    }
    return chip;
  };

  const renderManualLimitControls = (region: Region) => {
    if (!region.id) {
      return <Typography variant="body2" color="text.secondary">N/A</Typography>;
    }
    const isSaving = manualLimitSavingIds.has(region.id);
    const isIncreasing = quotaIncreaseIds.has(region.id);
    const pctLabel = `${Math.round((QUOTA_INCREASE_MULTIPLIER - 1) * 100)}%`;
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Manual bot limit"
          value={manualLimitDrafts[region.id] ?? ''}
          onChange={(e) => handleManualLimitChange(region.id || '', e.target.value)}
          onBlur={() => handleManualLimitSave(region)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleManualLimitSave(region);
            }
          }}
          type="number"
          size="small"
          inputProps={{ min: 0 }}
          disabled={isSaving}
          helperText="Blank = use synced limit"
          sx={{ width: 170, '& input': { fontFamily: '"Space Mono", monospace', fontWeight: 600 } }}
        />
        {reservationsSupported && (
          <Tooltip title={`Request a +${pctLabel} quota increase (vCPUs, public IPs, instances) from the provider`}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<TrendingUpIcon />}
                disabled={isIncreasing}
                onClick={() =>
                  requestConfirm({
                    title: 'Request quota increase',
                    message:
                      `Request a +${pctLabel} quota increase for ${region.region_code} ` +
                      `(${selectedProvider?.provider_type?.toUpperCase()})?\n\n` +
                      'This raises the vCPU, public IP and instance limits via the cloud ' +
                      'provider, which may open a support ticket on their side.',
                    confirmLabel: 'Request increase',
                    onConfirm: () => handleIncreaseQuota(region),
                  })
                }
                sx={{ whiteSpace: 'nowrap' }}
              >
                {isIncreasing ? 'Requesting…' : `Increase quota +${pctLabel}`}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderRegionDetail = (region: Region) => {
    const capacity = region.id ? capacityData.get(region.id) : undefined;
    const summary = region.id ? azureReservations.get(region.id) : undefined;
    const progress = region.id ? reservationProgress.get(region.id) : undefined;
    const busy =
      (region.id ? reservationBusyIds.has(region.id) : false) ||
      summary?.operation_status === 'queued' ||
      summary?.operation_status === 'in_progress';

    return (
      <Box sx={{ p: 2.5, backgroundColor: 'action.hover' }}>
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'stretch' }}>
          {/* Capacity & limits */}
          <Paper variant="outlined" sx={{ p: 2, flex: '1 1 340px', minWidth: 300 }}>
            <Typography variant="subtitle2" gutterBottom>
              Capacity &amp; limits
            </Typography>
            <CapacityDetail capacity={capacity} />
            <Divider sx={{ my: 1.5 }} />
            {renderManualLimitControls(region)}
            {region.location && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Location: {region.location}
                {region.continent ? ` · ${region.continent}` : ''}
              </Typography>
            )}
          </Paper>

          {/* Machine images */}
          <Paper variant="outlined" sx={{ p: 2, flex: '1 1 340px', minWidth: 300 }}>
            <Typography variant="subtitle2" gutterBottom>
              Machine images
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <ComputerIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    X86-64 (AMD64)
                  </Typography>
                </Box>
                {renderImageIdCell(region, 'image_id_amd64', 'amd64')}
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <MemoryIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    ARM64
                  </Typography>
                </Box>
                {renderImageIdCell(region, 'image_id_arm64', 'arm64')}
              </Box>
            </Box>
          </Paper>
        </Box>

        {reservationsSupported && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2.5 }}>
            <ReservationManager
              summary={summary}
              busy={busy}
              progress={progress}
              onReserve={(count, includeIpv6) => handleReserveReservations(region, count, includeIpv6)}
              onScaleDown={(count) => handleScaleDownReservations(region, count)}
              onRelease={(count) =>
                requestConfirm({
                  title: 'Release reservations',
                  message:
                    count === undefined
                      ? `Release all free reservations for ${region.region_code}? Claimed/assigned slots are not affected.`
                      : `Release ${count} free reservation(s) for ${region.region_code}? Claimed/assigned slots are not affected.`,
                  confirmLabel: 'Release',
                  confirmColor: 'error',
                  onConfirm: () => handleReleaseReservations(region, count),
                })
              }
              onReleaseSelected={(ids) =>
                requestConfirm({
                  title: 'Release selected reservations',
                  message: `Release ${ids.length} selected reservation(s) for ${region.region_code}? This permanently deletes their NIC/IP.`,
                  confirmLabel: 'Release',
                  confirmColor: 'error',
                  onConfirm: () => handleReleaseSelectedReservations(region, ids),
                })
              }
              onReclaim={() =>
                requestConfirm({
                  title: 'Reclaim orphaned reservations',
                  message:
                    `Reclaim orphaned reservations for ${region.region_code}? ` +
                    'This clears reservations whose bot no longer exists (terminated/deleted).',
                  confirmLabel: 'Reclaim',
                  confirmColor: 'error',
                  onConfirm: () => handleReclaimReservations(region),
                })
              }
              onRefresh={() => refreshRegionReservations(region)}
            />
          </Paper>
        )}
      </Box>
    );
  };

  const hasSelection = selectedRegions.size > 0;
  const selectedRegionsFreeReservations = regions.reduce((sum, region) => {
    if (!region.id || !selectedRegions.has(region.id)) return sum;
    return sum + (azureReservations.get(region.id)?.free ?? 0);
  }, 0);
  const totalColumns = reservationsSupported ? 9 : 8;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6">
          Region Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Provider</InputLabel>
            <Select
              value={providers.some((p) => p.id === selectedProviderId) ? selectedProviderId : ''}
              onChange={(e) => setSelectedProviderId(e.target.value)}
              label="Provider"
            >
              <MenuItem value="">
                <em>Select provider</em>
              </MenuItem>
              {providers.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name} ({provider.provider_type.toUpperCase()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            freeSolo
            options={regionFilterOptions}
            value={regionFilters}
            onChange={(_, value) => setRegionFilters(normalizeRegionFilters(value))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={`${option}-${index}`}
                  label={option}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Region Filter"
                placeholder="ap, us, eu"
                helperText={
                  normalizedRegionFilters.length
                    ? `Showing ${filteredRegions.length}/${regions.length}`
                    : undefined
                }
              />
            )}
            sx={{ minWidth: 260 }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadRegions}
            disabled={loading || !selectedProviderId}
          >
            Refresh
          </Button>
          {syncingCapacity ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                disabled
                sx={{ pointerEvents: 'none' }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  <Typography component="span" variant="body2">
                    {syncProgress
                      ? `Syncing ${syncProgress.synced}/${syncProgress.total}`
                      : 'Syncing'}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      '@keyframes syncPulse': {
                        '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                        '40%': { opacity: 1, transform: 'scale(1)' }
                      },
                      '& span': {
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        display: 'inline-block',
                        animation: 'syncPulse 1s infinite ease-in-out'
                      },
                      '& span:nth-of-type(2)': {
                        animationDelay: '0.2s'
                      },
                      '& span:nth-of-type(3)': {
                        animationDelay: '0.4s'
                      }
                    }}
                  >
                    <span />
                    <span />
                    <span />
                  </Box>
                </Box>
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<CancelIcon />}
                onClick={handleCancelSync}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={syncingRegions ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setSyncMenuAnchor(e.currentTarget)}
                disabled={loading || !selectedProviderId}
              >
                Sync
              </Button>
              <Menu
                anchorEl={syncMenuAnchor}
                open={Boolean(syncMenuAnchor)}
                onClose={() => setSyncMenuAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setSyncMenuAnchor(null);
                    handleSyncCapacity();
                  }}
                >
                  <ListItemIcon>
                    <MemoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sync Capacity"
                    secondary="Refresh quotas & instance limits"
                  />
                </MenuItem>
                <MenuItem
                  disabled={syncingRegions}
                  onClick={() => {
                    setSyncMenuAnchor(null);
                    handleSyncRegions();
                  }}
                >
                  <ListItemIcon>
                    {syncingRegions ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CloudIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={syncingRegions ? 'Checking regions…' : 'Sync Regions'}
                    secondary="Add / remove regions offered by the cloud"
                  />
                </MenuItem>
              </Menu>
            </>
          )}
          {reservationsSupported && (
            <Button
              variant="outlined"
              color="error"
              startIcon={releasingReservations ? <CircularProgress size={16} color="inherit" /> : <PowerIcon />}
              onClick={() => {
                const regionsWithFree = regions.filter((r) => {
                  const s = r.id ? azureReservations.get(r.id) : undefined;
                  return s !== undefined && s.free > 0;
                });
                const totalFree = regionsWithFree.reduce(
                  (sum, r) => sum + (azureReservations.get(r.id!)?.free ?? 0),
                  0,
                );
                requestConfirm({
                  title: 'Release all reservations',
                  message:
                    `Release all ${totalFree} free reservation(s) for ` +
                    `${selectedProvider?.name ?? 'this provider'} across ` +
                    `${regionsWithFree.length} region${regionsWithFree.length === 1 ? '' : 's'}?\n\n` +
                    'Claimed/assigned slots are not affected.',
                  confirmLabel: 'Release all',
                  confirmColor: 'error',
                  onConfirm: handleReleaseAllReservations,
                });
              }}
              disabled={loading || releasingReservations || !selectedProviderId || totalFreeReservations === 0}
            >
              {releasingReservations ? 'Releasing…' : 'Release All Reservations'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
            disabled={!selectedProviderId}
          >
            Create Region
          </Button>
          <Menu
            anchorEl={createMenuAnchor}
            open={Boolean(createMenuAnchor)}
            onClose={() => setCreateMenuAnchor(null)}
          >
            <MenuItem onClick={() => {
              setCreateMenuAnchor(null);
              setCreateDialogOpen(true);
            }}>
              <ListItemIcon>
                <AddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Create Single Region</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              setCreateMenuAnchor(null);
              setBulkCreateDialogOpen(true);
            }}>
              <ListItemIcon>
                <PlaylistAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Create Multiple Regions</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Bulk Actions */}
      <Box sx={{ mb: 2 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            backgroundColor: hasSelection ? 'action.selected' : 'background.default',
            opacity: hasSelection ? 1 : 0.7
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {hasSelection
              ? `${selectedRegions.size} region${selectedRegions.size === 1 ? '' : 's'} selected`
              : 'Select regions from the table to enable bulk actions.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {reservationsSupported && (
              <Button
                variant="outlined"
                startIcon={<CloudIcon />}
                onClick={() => setBulkReserveDialogOpen(true)}
                disabled={loading || !hasSelection}
                color="primary"
              >
                Bulk Reserve
              </Button>
            )}
            {reservationsSupported && (
              <Button
                variant="outlined"
                color="error"
                startIcon={releasingReservations ? <CircularProgress size={16} color="inherit" /> : <PowerIcon />}
                onClick={() => {
                  const regionNames = regions
                    .filter((r) => r.id && selectedRegions.has(r.id) && (azureReservations.get(r.id)?.free ?? 0) > 0)
                    .map((r) => r.region_code);
                  requestConfirm({
                    title: 'Release reservations',
                    message:
                      `Release all ${selectedRegionsFreeReservations} free reservation(s) across ` +
                      `${regionNames.length} selected region${regionNames.length === 1 ? '' : 's'}?\n\n` +
                      `${regionNames.join(', ')}\n\nClaimed/assigned slots are not affected.`,
                    confirmLabel: 'Release',
                    confirmColor: 'error',
                    onConfirm: handleReleaseSelectedRegionsReservations,
                  });
                }}
                disabled={loading || releasingReservations || !hasSelection || selectedRegionsFreeReservations === 0}
              >
                Release Reservations
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<PowerIcon />}
              onClick={() => {
                const allEnabled = regions.filter(r => r.id && selectedRegions.has(r.id)).every(r => r.configured);
                const action = allEnabled ? 'disable' : 'enable';
                requestConfirm({
                  title: `${action === 'enable' ? 'Enable' : 'Disable'} regions`,
                  message: `${action === 'enable' ? 'Enable' : 'Disable'} ${selectedRegions.size} selected region(s)?`,
                  confirmLabel: action === 'enable' ? 'Enable' : 'Disable',
                  onConfirm: handleBulkToggleConfigured,
                });
              }}
              disabled={loading || !hasSelection}
              color="primary"
            >
              {regions.filter(r => r.id && selectedRegions.has(r.id)).every(r => r.configured) ? 'Disable Selected' : 'Enable Selected'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => {
                const regionNames = regions
                  .filter(r => r.id && selectedRegions.has(r.id))
                  .map(r => r.name);
                requestConfirm({
                  title: 'Delete regions',
                  message: `Permanently delete ${selectedRegions.size} region(s)?\n\n${regionNames.join('\n')}`,
                  confirmLabel: 'Delete',
                  confirmColor: 'error',
                  onConfirm: handleBulkDelete,
                });
              }}
              disabled={loading || !hasSelection}
              color="error"
            >
              Delete Selected
            </Button>
          </Box>
        </Paper>
      </Box>

      {loading && <LinearProgress />}

      {!selectedProviderId ? (
        <Alert severity="info">
          Please select a provider to manage regions.
        </Alert>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 44 }} />
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRegions.size === filteredRegions.length && filteredRegions.length > 0}
                      indeterminate={selectedRegions.size > 0 && selectedRegions.size < filteredRegions.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'region_code'}
                      direction={sortField === 'region_code' ? sortDirection : 'asc'}
                      onClick={() => handleSort('region_code')}
                    >
                      Region
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'capacity'}
                      direction={sortField === 'capacity' ? sortDirection : 'asc'}
                      onClick={() => handleSort('capacity')}
                    >
                      Limit
                    </TableSortLabel>
                  </TableCell>
                  {reservationsSupported && (
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'reservations'}
                        direction={sortField === 'reservations' ? sortDirection : 'asc'}
                        onClick={() => handleSort('reservations')}
                      >
                        Reservations
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'machine_count'}
                      direction={sortField === 'machine_count' ? sortDirection : 'asc'}
                      onClick={() => handleSort('machine_count')}
                    >
                      Bots
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'configured'}
                      direction={sortField === 'configured' ? sortDirection : 'asc'}
                      onClick={() => handleSort('configured')}
                    >
                      Enabled
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...filteredRegions]
                  .sort((a, b) => {
                    const dir = sortDirection === 'asc' ? 1 : -1;
                    // Status severity order: available (green) first, then
                    // cooldown, unavailable, and finally not-synced regions.
                    const statusRank: Record<string, number> = { green: 0, yellow: 1, red: 2 };
                    const sortValue = (region: Region): string | number => {
                      switch (sortField) {
                        case 'region_code':
                          return region.region_code ?? '';
                        case 'status': {
                          const capacity = region.id ? capacityData.get(region.id) : undefined;
                          return capacity ? statusRank[capacity.status] ?? 3 : 4;
                        }
                        case 'capacity': {
                          // Sort the Limit column by the region's effective bot
                          // limit (manual override when set, else synced quota).
                          const capacity = region.id ? capacityData.get(region.id) : undefined;
                          const manualLimit = capacity?.manual_bot_limit;
                          const limit =
                            manualLimit === null || manualLimit === undefined
                              ? capacity?.effective_instance_limit ?? capacity?.instance_limit
                              : manualLimit;
                          return typeof limit === 'number' ? limit : -1;
                        }
                        case 'reservations': {
                          const summary = region.id ? azureReservations.get(region.id) : undefined;
                          return summary?.total ?? -1;
                        }
                        case 'machine_count':
                          return region.machine_count ?? 0;
                        case 'configured':
                          return region.configured ? 1 : 0;
                        default:
                          return '';
                      }
                    };
                    const aValue = sortValue(a);
                    const bValue = sortValue(b);
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                      return dir * aValue.localeCompare(bValue);
                    }
                    return dir * ((aValue as number) - (bValue as number));
                  })
                  .map((region) => {
                    const isExpanded = region.id ? expandedRegions.has(region.id) : false;
                    const capacity = region.id ? capacityData.get(region.id) : undefined;
                    const summary = region.id ? azureReservations.get(region.id) : undefined;
                    return (
                  <React.Fragment key={region.id}>
                    <TableRow hover sx={{ '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}>
                      <TableCell padding="checkbox">
                        <Tooltip title={isExpanded ? 'Hide details' : 'Show details'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => toggleRegionExpanded(region.id)}
                              disabled={!region.id}
                            >
                              {isExpanded ? <ExpandMoreIcon /> : <ExpandRightIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={region.id ? selectedRegions.has(region.id) : false}
                          onChange={() => handleSelectRegion(region.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ cursor: region.id ? 'pointer' : 'default' }}
                          onClick={() => toggleRegionExpanded(region.id)}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {region.region_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {region.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{renderStatusChip(region)}</TableCell>
                      <TableCell>
                        <RegionLimitCell capacity={capacity} />
                      </TableCell>
                      {reservationsSupported && (
                        <TableCell>
                          <ReservationSummaryCell
                            summary={summary}
                            progress={region.id ? reservationProgress.get(region.id) : undefined}
                          />
                        </TableCell>
                      )}
                      <TableCell>{region.machine_count}</TableCell>
                      <TableCell>
                        <Switch
                          checked={region.configured}
                          onChange={() => handleToggleConfigured(region)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingRegion(region);
                                setEditForm({
                                  name: region.name,
                                  region_code: region.region_code,
                                  location: region.location || '',
                                  continent: region.continent || '',
                                  configured: region.configured,
                                  image_id_amd64: region.image_id_amd64 || '',
                                  image_id_arm64: region.image_id_arm64 || ''
                                });
                                setEditDialogOpen(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() =>
                                requestConfirm({
                                  title: 'Delete region',
                                  message: `Permanently delete region "${region.name}" (${region.region_code})?`,
                                  confirmLabel: 'Delete',
                                  confirmColor: 'error',
                                  onConfirm: () => handleDeleteRegion(region),
                                })
                              }
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={totalColumns} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          {renderRegionDetail(region)}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                    );
                  })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={3} sx={{ borderBottom: 'none' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} color="text.secondary">
                      Totals
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }} />
                  <TableCell sx={{ borderBottom: 'none' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 150 }}>
                        <Typography variant="caption" color="text.secondary">Total capacity</Typography>
                        <Typography variant="caption" sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600 }}>
                          {capacityTotals.totalCapacity}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 150 }}>
                        <Typography variant="caption" color="text.secondary">Enabled capacity</Typography>
                        <Typography variant="caption" sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600 }}>
                          {capacityTotals.enabledCapacity}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {reservationsSupported && (
                    <TableCell sx={{ borderBottom: 'none' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 120 }}>
                        <Typography variant="caption" color="text.secondary">Reserved capacity</Typography>
                        <Typography variant="caption" sx={{ fontFamily: '"Space Mono", monospace', fontWeight: 600 }}>
                          {capacityTotals.reservedCapacity}
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell colSpan={3} sx={{ borderBottom: 'none' }} />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Cloud-provider error history for the selected provider */}
      {selectedProviderId && (
        <Box sx={{ mt: 3 }}>
          <ProviderErrorsPanel providerId={selectedProviderId} />
        </Box>
      )}

      {/* Create Region Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Region</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Region Code"
                value={createForm.region_code}
                onChange={(e) => setCreateForm({ ...createForm, region_code: e.target.value })}
                fullWidth
                required
              />
              <Tooltip title="Auto-fill region name and provider region code">
                <IconButton
                  onClick={handleAutoFillRegion}
                  disabled={!createForm.region_code || !selectedProviderId}
                  color="primary"
                  sx={{ mt: 1 }}
                >
                  <AutoFillIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              label="Region Name"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Image ID (AMD64)"
              value={createForm.image_id_amd64}
              onChange={(e) => setCreateForm({ ...createForm, image_id_amd64: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <ComputerIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <TextField
              label="Image ID (ARM64)"
              value={createForm.image_id_arm64}
              onChange={(e) => setCreateForm({ ...createForm, image_id_arm64: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <MemoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <TextField
              label="Location"
              value={createForm.location}
              onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
              fullWidth
            />
            <TextField
              label="Continent"
              value={createForm.continent}
              onChange={(e) => setCreateForm({ ...createForm, continent: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={createForm.configured}
                  onChange={(e) => setCreateForm({ ...createForm, configured: e.target.checked })}
                />
              }
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateRegion} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Region Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Region</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Region Name"
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Region Code"
              value={editForm.region_code || ''}
              onChange={(e) => setEditForm({ ...editForm, region_code: e.target.value })}
              fullWidth
            />
            <TextField
              label="Image ID (AMD64)"
              value={editForm.image_id_amd64 || ''}
              onChange={(e) => setEditForm({ ...editForm, image_id_amd64: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <ComputerIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <TextField
              label="Image ID (ARM64)"
              value={editForm.image_id_arm64 || ''}
              onChange={(e) => setEditForm({ ...editForm, image_id_arm64: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <MemoryIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <TextField
              label="Location"
              value={editForm.location || ''}
              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
              fullWidth
            />
            <TextField
              label="Continent"
              value={editForm.continent || ''}
              onChange={(e) => setEditForm({ ...editForm, continent: e.target.value })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.configured ?? false}
                  onChange={(e) => setEditForm({ ...editForm, configured: e.target.checked })}
                />
              }
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditRegion} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Create Regions Dialog */}
      <Dialog open={bulkCreateDialogOpen} onClose={() => setBulkCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Multiple Regions</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Enter region codes, one per line. Each region will be created with auto-filled values:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
              • Region Name = Region Code
            </Typography>
            <TextField
              label="Region Codes"
              value={bulkRegionsText}
              onChange={(e) => setBulkRegionsText(e.target.value)}
              multiline
              rows={10}
              fullWidth
              placeholder="us-east-1&#10;us-west-2&#10;eu-west-1&#10;ap-south-1"
              helperText="Enter one region code per line"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBulkCreateDialogOpen(false);
            setBulkRegionsText('');
          }}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkCreateRegions} 
            variant="contained"
            disabled={!bulkRegionsText.trim() || loading}
          >
            {loading ? 'Creating...' : 'Add Regions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Reserve Dialog */}
      <Dialog
        open={bulkReserveDialogOpen}
        onClose={() => {
          setBulkReserveDialogOpen(false);
          setBulkReserveMaxMode(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Reserve Capacity</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Reserve a total number of network slots across the{' '}
              {bulkReserveRegions.length} selected region
              {bulkReserveRegions.length === 1 ? '' : 's'}. The total is divided
              as evenly as possible; any remainder goes to the first regions. Or
              use “Fill max available” to reserve each region up to its remaining
              capacity.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                label="Total slots"
                type="number"
                value={bulkReserveTotal}
                onChange={(e) => {
                  // Manual edits leave max mode — the total is divided equally again.
                  setBulkReserveMaxMode(false);
                  setBulkReserveTotal(e.target.value);
                }}
                fullWidth
                autoFocus
                inputProps={{ min: 1, step: 1 }}
                helperText={
                  bulkReserveRegions.length === 0
                    ? 'Select regions from the table first'
                    : bulkReserveMaxMode
                      ? 'Filling each region to its available capacity'
                      : `Divided across ${bulkReserveRegions.length} region${bulkReserveRegions.length === 1 ? '' : 's'}`
                }
              />
              <Button
                variant="outlined"
                sx={{ mt: 1, whiteSpace: 'nowrap', flexShrink: 0 }}
                disabled={bulkReserveMaxAvailable === 0}
                onClick={() => {
                  setBulkReserveMaxMode(true);
                  setBulkReserveTotal(String(bulkReserveMaxAvailable));
                }}
              >
                Fill max available
              </Button>
            </Box>
            {bulkReserveRegions.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                {bulkReserveMaxAvailable} slot{bulkReserveMaxAvailable === 1 ? '' : 's'} available
                across the selected region{bulkReserveRegions.length === 1 ? '' : 's'}
                {bulkReserveUnsyncedCount > 0
                  ? ` · ${bulkReserveUnsyncedCount} region${bulkReserveUnsyncedCount === 1 ? '' : 's'} not synced (excluded — run “Sync Capacity”)`
                  : ''}
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkReserveIncludeIpv6}
                  onChange={(e) => setBulkReserveIncludeIpv6(e.target.checked)}
                />
              }
              label="Include IPv6 public IP"
            />
            {bulkReservePlan.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Allocation preview
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 240, overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Region</TableCell>
                        <TableCell align="right">Available</TableCell>
                        <TableCell align="right">Slots</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkReservePlan.map(({ region, count }) => {
                        const available = availableCapacityForRegion(region.id);
                        const overCapacity = available !== null && count > available;
                        return (
                          <TableRow key={region.id}>
                            <TableCell>{region.region_code}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {available === null ? '—' : available}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {count === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                  skipped
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color={overCapacity ? 'warning.main' : 'text.primary'}
                                  title={overCapacity ? 'Exceeds this region’s available capacity' : undefined}
                                >
                                  {count}
                                  {overCapacity ? ' ⚠' : ''}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkReserveDialogOpen(false);
              setBulkReserveMaxMode(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkReserve}
            variant="contained"
            disabled={bulkReservePlan.filter((entry) => entry.count > 0).length === 0}
          >
            Reserve
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmState?.open ?? false}
        title={confirmState?.title ?? ''}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        confirmColor={confirmState?.confirmColor}
        onConfirm={() => {
          confirmState?.onConfirm();
          closeConfirm();
        }}
        onClose={closeConfirm}
      />

    </Box>
  );
};

export default RegionManagement;
